import {
  acknowledgeAgentServiceFailure,
  acceptAgentServiceHuman,
  disableAgentServiceAi,
  cancelAgentServiceReply,
  enableAgentServiceAi,
  getAgentServiceConversation,
  getAgentServiceMessages,
  rejectAgentServiceHandoff,
  releaseAgentServiceToAi,
  requestAgentServiceHuman,
  resolveAgentServiceConversation,
  sendAgentServiceMessage,
  updateAgentServiceReadCursor,
} from "@/api/agentService";
import {
  adaptAgentServiceConversationView,
  adaptAgentServiceMessage,
  adaptAgentServiceWebSocketNotification,
} from "@/services/agent-service-contract";
import {
  createAgentServiceClientMessageId,
  deriveAgentServiceAgentProcessing,
  lastAgentServiceSequence,
  mergeAgentServiceMessages,
  requireAgentServiceSuccess,
  reduceAgentServiceNotification,
  resolveAgentServiceControlPolicy,
} from "@/services/agent-service-state";

const initialState = () => ({
  conversation: null,
  viewerRole: '',
  messages: [],
  nextBeforeSequence: null,
  hasMore: false,
  loading: false,
  loadingOlder: false,
  sending: false,
  controlChanging: false,
  controlChangingConversationId: "",
  realtimeState: "CLOSED",
  agentProcessing: false,
  cancelledThroughSequence: 0,
  activeAgentRunId: null,
  terminalAgentRunIds: {},
  agentError: null,
  agentTerminalMessageId: "",
  agentStatusUnavailableCount: 0,
  agentStatusUnavailableSince: null,
  agentStatusMessageId: "",
  failureNoticeAttempts: {},
  error: "",
  openEpoch: 0,
  requestedConversationId: "",
  visible: true,
  conversationMissing: false,
  // 消息中心在联系人间切换时复用最近一次权威快照；网络请求只负责后台校准。
  conversationCache: {},
});

const responseData = (response, fallbackMessage) => {
  if (!response || response.code !== 200 || response.data === undefined) {
    const error = new Error(response?.msg || fallbackMessage);
    error.code = response?.code;
    error.errorCode = response?.errorCode;
    throw error;
  }
  return response.data;
};

const currentDisplayName = (conversation, currentUserId) =>
  String(currentUserId) === conversation.customerUserId
    ? conversation.customerDisplayName
    : conversation.assignedStaffDisplayName;

const cacheableConversationState = (state) => ({
  conversation: state.conversation,
  messages: state.messages,
  nextBeforeSequence: state.nextBeforeSequence,
  hasMore: state.hasMore,
  agentProcessing: state.agentProcessing,
  cancelledThroughSequence: state.cancelledThroughSequence,
  agentError: state.agentError,
  agentTerminalMessageId: state.agentTerminalMessageId,
  agentStatusMessageId: state.agentStatusMessageId,
  failureNoticeAttempts: state.failureNoticeAttempts,
  activeAgentRunId: state.activeAgentRunId,
  terminalAgentRunIds: state.terminalAgentRunIds,
});

/** 保存当前会话的纯展示快照，避免切换联系人时先清空再闪烁加载态。 */
const rememberConversationState = (state) => {
  const conversationId = String(state.conversation?.conversationId || "");
  if (!conversationId) return;
  state.conversationCache = {
    ...(state.conversationCache || {}),
    [conversationId]: cacheableConversationState(state),
  };
};

const applyControlResult = (conversation, result) => ({
  ...conversation,
  controlState: result.controlState,
  controlVersion: result.controlVersion,
  handoffReason: result.handoffReason || null,
  handoffRequestedAt: result.handoffRequestedAt || null,
  humanAcceptedAt: result.humanAcceptedAt || null,
});

const isCurrentConversationRequest = (state, epoch, conversationId) => {
  if (state.openEpoch !== epoch) return false;
  const activeConversationId = String(
    state.conversation?.conversationId || state.requestedConversationId || "",
  );
  return !conversationId || activeConversationId === String(conversationId);
};

const latestPersistedCustomerMessage = (state) => state.messages
  .filter(message => message.actorType === 'CUSTOMER' && !message.localStatus && Number(message.sequence) > 0)
  .reduce((latest, message) => Number(message.sequence) > Number(latest?.sequence || 0) ? message : latest, null);

/** 复用消息/控制协议实现，但为每个领域生成独立状态；GENERAL不接受订单锚点。 */
export function createAgentServiceStore({ sceneType } = {}) {
  const adaptConversation = (raw) => {
    const conversation = adaptAgentServiceConversationView(raw);
    if (sceneType && conversation.scene?.sceneType !== sceneType) {
      throw new Error("总客服不能加载商品或订单会话");
    }
    return conversation;
  };
  const adaptPage = (page, conversation) => {
    const items = (page.items || []).map(adaptAgentServiceMessage);
    if (
      sceneType === "GENERAL" &&
      items.some(
        (message) =>
          message.conversationId !== conversation.conversationId ||
          message.tenantId !== conversation.tenantId ||
          (message.actorType === "CUSTOMER" &&
            message.actorUserId !== conversation.customerUserId) ||
          (message.actorType === "BUSINESS_STAFF" &&
            message.actorUserId !== conversation.assignedStaffUserId),
      )
    )
      throw new Error("总客服消息不属于当前会话或参与者");
    return { ...page, items };
  };
  // 取消会改变服务端控制版本；跨窗口重开也必须等待同会话旧取消完成，不能只依赖当前Vue实例。
  const replyCancellationFlights = new Map();
  let noticeInFlight = null;
  const module = {
    namespaced: true,
    state: () => ({ ...initialState(), visible: sceneType !== "GENERAL" }),
    getters: {
      /** 返回当前用户在该会话中的可见操作。 */
      controlPolicy: (state) => (currentUserId) =>
        resolveAgentServiceControlPolicy(state.conversation, currentUserId, state.viewerRole),
    },
    mutations: {
      setViewerRole(state, role) { state.viewerRole = role === 'RECEPTION' ? role : ''; },
      incrementAgentStatusUnavailable(state) {
        if (!state.agentStatusUnavailableCount) state.agentStatusUnavailableSince = Date.now();
        state.agentStatusUnavailableCount++;
      },
      clearAgentStatusUnavailable(state) {
        state.agentStatusUnavailableCount = 0;
        state.agentStatusUnavailableSince = null;
        if (state.agentError?.code === 'AGENT_STATUS_UNAVAILABLE') state.agentError = null;
      },
      showAgentStatusUnavailable(state, notice) {
        // 状态查询失败不等于模型失败；继续接收回复，不写系统失败消息或递增控制版本。
        state.agentError = { code: 'AGENT_STATUS_UNAVAILABLE', message: notice || '网络繁忙请稍后再试', retryable: true };
      },
      incrementFailureNoticeAttempt(state, id) { state.failureNoticeAttempts = { ...state.failureNoticeAttempts, [id]: (state.failureNoticeAttempts[id] || 0) + 1 }; },
      finishAgentRequest(state, { businessMessageId, notice, failed = false, agentRunId }) {
        const source = latestPersistedCustomerMessage(state);
        if (!source || source.businessMessageId !== businessMessageId) return;
        state.agentTerminalMessageId = businessMessageId;
        state.agentProcessing = false;
        state.activeAgentRunId = null;
        if (agentRunId) state.terminalAgentRunIds = { ...state.terminalAgentRunIds, [agentRunId]: true };
        state.agentError = failed ? { code: 'DEPENDENCY_UNAVAILABLE', message: notice || '网络繁忙请稍后再试', retryable: true } : null;
        rememberConversationState(state);
      },
      /** 关闭窗口立即使旧REST响应失效，保留权威消息与会话缓存供下次打开。 */
      pauseReception(state) {
        state.openEpoch++;
        state.visible = false;
        state.loading = false;
        state.sending = false;
        state.loadingOlder = false;
      },
      /** 取消只作用于当前问题，后续更高sequence的新问题仍可正常生成。 */
      cancelPendingReplyLocal(state, throughSequence) {
        state.cancelledThroughSequence = Math.max(state.cancelledThroughSequence || 0, throughSequence);
        state.agentProcessing = false;
        if (state.activeAgentRunId) state.terminalAgentRunIds[state.activeAgentRunId] = true;
        state.activeAgentRunId = null;
        rememberConversationState(state);
      },
      applyReplyCancellation(state, { conversationId, controlVersion }) {
        const cached = state.conversationCache[conversationId];
        if (cached?.conversation && cached.conversation.controlVersion <= controlVersion)
          cached.conversation = { ...cached.conversation, controlVersion };
        if (state.conversation?.conversationId === conversationId && state.conversation.controlVersion <= controlVersion)
          state.conversation = { ...state.conversation, controlVersion };
      },
      /** 取消请求失败不能伪造已取消；仅恢复原版本，不覆盖后来切换的会话或控制权。 */
      rejectReplyCancellation(state, { conversationId, expectedControlVersion, previousSequence }) {
        const cached = state.conversationCache[conversationId];
        if (cached?.conversation?.controlVersion === expectedControlVersion) {
          cached.cancelledThroughSequence = previousSequence;
          cached.agentProcessing = deriveAgentServiceAgentProcessing(cached.conversation, cached.messages, false, previousSequence);
        }
        if (state.conversation?.conversationId === conversationId && state.conversation.controlVersion === expectedControlVersion) {
          state.cancelledThroughSequence = previousSequence;
          state.agentProcessing = deriveAgentServiceAgentProcessing(state.conversation, state.messages, false, previousSequence);
        }
      },
      clearIdentity(state) {
        Object.assign(state, initialState(), { openEpoch: state.openEpoch + 1, visible: sceneType !== 'GENERAL' });
      },
      reset(state) {
        const openEpoch = Number(state.openEpoch || 0) + 1;
        rememberConversationState(state);
        const conversationCache = state.conversationCache;
        Object.assign(state, initialState(), {
          openEpoch,
          visible: sceneType !== "GENERAL",
          conversationCache,
        });
      },
      /** 收起只改变可见性，不清空草稿或活动会话；隐藏补读不推进客户已读。 */
      setVisible(state, value) {
        state.visible = Boolean(value);
      },
      /** 404使旧请求及旧ID失效，不通过自动resolve重新创建已删除的会话。 */
      markConversationMissing(state) {
        const openEpoch = state.openEpoch + 1;
        const visible = state.visible;
        Object.assign(state, initialState(), {
          openEpoch,
          visible,
          conversationMissing: true,
          error: "会话已不存在，请重新打开客服窗口。",
        });
      },
      beginConversationOpen(state, conversationId) {
        const openEpoch = Number(state.openEpoch || 0) + 1;
        const visible = state.visible;
        rememberConversationState(state);
        const conversationCache = state.conversationCache || {};
        const cached = conversationCache[String(conversationId || "")];
        Object.assign(state, initialState(), {
          ...(cached || {}),
          openEpoch,
          visible,
          conversationCache,
          requestedConversationId: String(conversationId || ""),
          loading: true,
        });
      },
      setRequestedConversationId(state, conversationId) {
        state.requestedConversationId = String(conversationId || "");
      },
      setLoading(state, loading) {
        state.loading = loading;
      },
      setLoadingOlder(state, loading) {
        state.loadingOlder = loading;
      },
      setSending(state, sending) {
        state.sending = sending;
      },
      setControlChanging(state, conversationId) {
        state.controlChanging = Boolean(conversationId);
        state.controlChangingConversationId = conversationId
          ? String(conversationId)
          : "";
      },
      setConversation(state, conversation) {
        const previous = state.conversation;
        const same = previous?.conversationId === conversation?.conversationId;
        if (same && conversation.controlVersion < previous.controlVersion) return;
        const invalidated = same && previous?.controlState === 'AI_ENABLED'
          && conversation?.controlState === 'AI_ENABLED' && conversation.controlVersion > previous.controlVersion
          && (state.agentProcessing || state.sending);
        state.cancelledThroughSequence = state.conversationCache[conversation?.conversationId]?.cancelledThroughSequence || 0;
        state.conversation = conversation;
        // AI→AI版本递增代表本轮已失效。REST恢复必须与实时取消通知一致，不能保留旧转圈。
        if (invalidated) {
          state.cancelledThroughSequence = Math.max(state.cancelledThroughSequence,
            Number(latestPersistedCustomerMessage(state)?.sequence || 0));
          state.agentProcessing = false;
          if (state.activeAgentRunId) state.terminalAgentRunIds = { ...state.terminalAgentRunIds, [state.activeAgentRunId]: true };
          state.activeAgentRunId = null;
        }
        if (
          conversation?.controlState !== 'AI_ENABLED'
        ) {
          state.agentProcessing = false;
          state.activeAgentRunId = null;
        }
        rememberConversationState(state);
      },
      setMessagePage(state, page) {
        const previousSequence = lastAgentServiceSequence(state.messages);
        state.messages = mergeAgentServiceMessages(
          state.messages,
          page.items,
        );
        state.nextBeforeSequence = page.nextBeforeSequence || null;
        state.hasMore = Boolean(page.hasMore);
        const terminalAgentRunIds = { ...(state.terminalAgentRunIds || {}) };
        state.messages.forEach((message) => {
          if (message.actorType === "AI_AGENT" && message.agentRunId) {
            terminalAgentRunIds[message.agentRunId] = true;
          }
        });
        state.terminalAgentRunIds = terminalAgentRunIds;
        const nextSequence = lastAgentServiceSequence(state.messages);
        if (nextSequence > previousSequence) {
          state.agentProcessing = deriveAgentServiceAgentProcessing(
            state.conversation,
            state.messages,
            state.agentProcessing,
            state.cancelledThroughSequence,
          );
        }
        const latestMessage = state.messages.reduce(
          (latest, message) =>
            Number(message.sequence || 0) > Number(latest?.sequence || 0)
              ? message
              : latest,
          null,
        );
        if (["AI_AGENT", "BUSINESS_STAFF"].includes(latestMessage?.actorType)) {
          state.agentProcessing = false;
          state.activeAgentRunId = null;
        }
        if (latestMessage?.actorType === "AI_AGENT" ||
          (latestMessage?.actorType === "BUSINESS_STAFF" && state.agentError?.code === 'AGENT_STATUS_UNAVAILABLE')) {
          state.agentError = null;
        }
        const latestCustomer = latestPersistedCustomerMessage(state);
        if (latestCustomer?.businessMessageId !== state.agentStatusMessageId) {
          state.agentStatusMessageId = latestCustomer?.businessMessageId || '';
          state.agentStatusUnavailableCount = 0;
          state.agentStatusUnavailableSince = null;
          if (state.agentError?.code === 'AGENT_STATUS_UNAVAILABLE') state.agentError = null;
        }
        if (latestCustomer?.businessMessageId === state.agentTerminalMessageId) state.agentProcessing = false;
        else if (latestCustomer && state.agentTerminalMessageId) {
          state.agentTerminalMessageId = '';
          state.agentStatusUnavailableCount = 0;
          state.agentStatusUnavailableSince = null;
          state.agentError = null;
        }
        rememberConversationState(state);
      },
      addPendingMessage(state, message) {
        state.messages = mergeAgentServiceMessages(state.messages, [
          message,
        ]);
        state.agentProcessing = deriveAgentServiceAgentProcessing(
          state.conversation,
          state.messages,
          false,
          state.cancelledThroughSequence,
        );
        rememberConversationState(state);
      },
      markPendingFailed(state, clientMessageId) {
        state.messages = state.messages.map((message) =>
          message.clientMessageId === clientMessageId
            ? { ...message, localStatus: "FAILED" }
            : message,
        );
        state.agentProcessing = deriveAgentServiceAgentProcessing(
          state.conversation,
          state.messages,
          false,
          state.cancelledThroughSequence,
        );
        rememberConversationState(state);
      },
      removePendingMessage(state, clientMessageId) {
        state.messages = state.messages.filter(
          (message) =>
            !(
              message.clientMessageId === clientMessageId && message.localStatus
            ),
        );
        rememberConversationState(state);
      },
      setRealtimeState(state, realtimeState) {
        state.realtimeState = realtimeState;
      },
      setAgentProcessing(state, payload) {
        const signal =
          payload && typeof payload === "object"
            ? payload
            : { value: payload, agentRunId: null };
        const runId = String(signal.agentRunId || "");
        const canRun = state.conversation?.controlState === 'AI_ENABLED';
        if (signal.value) {
          const source = latestPersistedCustomerMessage(state);
          if (!canRun || (runId && state.terminalAgentRunIds?.[runId]) ||
            (!state.sending && source && (source.sequence <= state.cancelledThroughSequence
              || source.businessMessageId === state.agentTerminalMessageId))) {
            return;
          }
          state.agentError = null;
          state.agentProcessing = true;
          state.activeAgentRunId = runId || null;
          return;
        }
        if (runId) {
          const wasAlreadyTerminal = Boolean(
            state.terminalAgentRunIds?.[runId],
          );
          state.terminalAgentRunIds = {
            ...(state.terminalAgentRunIds || {}),
            [runId]: true,
          };
          if (wasAlreadyTerminal && state.activeAgentRunId !== runId) return;
          if (state.activeAgentRunId && state.activeAgentRunId !== runId)
            return;
        }
        state.agentProcessing = false;
        state.activeAgentRunId = null;
      },
      setAgentError(state, payload) {
        if (payload?.error) {
          const runId = String(payload.agentRunId || "");
          if (
            (runId && state.terminalAgentRunIds?.[runId]) ||
            (state.activeAgentRunId && state.activeAgentRunId !== runId)
          ) {
            return;
          }
          state.agentError = payload.error;
          return;
        }
        state.agentError = payload;
      },
      setError(state, error) {
        state.error = error || "";
      },
    },
    actions: {
      /** 关闭未完成回答：先冻结本地接收，再按当前控制版本取消，绝不自动重发问题。 */
      async cancelPendingReply({ state, commit }) {
        const conversation = state.conversation;
        const pending = replyCancellationFlights.get(conversation?.conversationId);
        if (pending) return pending;
        if (!conversation || conversation.controlState !== 'AI_ENABLED' || state.controlChanging || (!state.agentProcessing && !state.sending)) return null;
        const through = Math.max(Number(conversation.lastSequence || 0), lastAgentServiceSequence(state.messages)) + (state.sending ? 1 : 0);
        const previousSequence = state.cancelledThroughSequence || 0;
        commit('cancelPendingReplyLocal', through);
        const operation = (async () => { try {
          const response = await cancelAgentServiceReply(conversation.conversationId, { expectedControlVersion: conversation.controlVersion });
          const result = responseData(response, '客服控制状态更新失败');
          commit('applyReplyCancellation', { conversationId: conversation.conversationId, controlVersion: result.controlVersion });
          return result;
        } catch (error) {
          commit('rejectReplyCancellation', { conversationId: conversation.conversationId, expectedControlVersion: conversation.controlVersion, previousSequence });
          throw error;
        } })();
        replyCancellationFlights.set(conversation.conversationId, operation);
        try { return await operation; }
        finally {
          if (replyCancellationFlights.get(conversation.conversationId) === operation)
            replyCancellationFlights.delete(conversation.conversationId);
        }
      },
      /** 按稳定会话ID打开消息中心中的既有正式客服会话。 */
      async openConversation({ state, commit, dispatch }, target) {
        const conversationId = typeof target === 'object' ? target.conversationId : target;
        commit("beginConversationOpen", conversationId);
        commit('setViewerRole', typeof target === 'object' ? target.viewerRole : '');
        const openEpoch = state.openEpoch;
        try {
          const cancellation = replyCancellationFlights.get(String(conversationId));
          if (cancellation) await cancellation;
          if (!isCurrentConversationRequest(state, openEpoch, conversationId)) return null;
          const response = await getAgentServiceConversation(conversationId);
          const conversation = adaptConversation(
            responseData(response, "无法打开订单客服会话"),
          );
          if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
            return null;
          }
          commit("setConversation", conversation);
          await dispatch("refreshMessages", { conversationId, openEpoch });
          if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
            return null;
          }
          return conversation;
        } catch (error) {
          if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
            return null;
          }
          commit("setError", error.message || "订单客服会话加载失败");
          throw error;
        } finally {
          if (isCurrentConversationRequest(state, openEpoch, conversationId)) {
            commit("setLoading", false);
          }
        }
      },

      /** 解析当前业务锚点并加载最近消息。 */
      async initialize({ state, commit, dispatch }, scene) {
        if (
          sceneType &&
          (scene?.sceneType !== sceneType ||
            Object.keys(scene).some((key) => key !== "sceneType"))
        ) {
          throw new Error("总客服初始化仅允许GENERAL场景");
        }
        commit("beginConversationOpen", "");
        const openEpoch = state.openEpoch;
        try {
          const response = await resolveAgentServiceConversation(scene);
          let conversation = adaptConversation(
            responseData(response, "无法建立订单客服会话"),
          );
          const cancellation = replyCancellationFlights.get(conversation.conversationId);
          if (cancellation) {
            await cancellation;
            if (state.openEpoch !== openEpoch) return null;
            conversation = adaptConversation(responseData(await getAgentServiceConversation(conversation.conversationId), "无法刷新客服会话"));
          }
          if (state.openEpoch !== openEpoch) return null;
          commit("setRequestedConversationId", conversation.conversationId);
          commit("setConversation", conversation);
          await dispatch("refreshMessages", {
            conversationId: conversation.conversationId,
            openEpoch,
          });
          if (
            !isCurrentConversationRequest(
              state,
              openEpoch,
              conversation.conversationId,
            )
          ) {
            return null;
          }
          return conversation;
        } catch (error) {
          if (state.openEpoch !== openEpoch) return null;
          commit("setError", error.message || "客服会话加载失败");
          throw error;
        } finally {
          if (state.openEpoch === openEpoch) commit("setLoading", false);
        }
      },

      /** 重新读取会话控制状态和参与者信息。 */
      async refreshConversation({ state, commit }) {
        if (!state.conversation?.conversationId) return null;
        const conversationId = state.conversation.conversationId;
        const openEpoch = state.openEpoch;
        const response = await getAgentServiceConversation(conversationId);
        const conversation = adaptConversation(
          responseData(response, "无法刷新客服会话"),
        );
        if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
          return null;
        }
        commit("setConversation", conversation);
        return conversation;
      },

      /** 读取最近一页MySQL权威消息并与当前列表去重。 */
      async refreshMessages({ state, commit, dispatch }, options = {}) {
        const conversationId =
          options.conversationId || state.conversation?.conversationId;
        const openEpoch = options.openEpoch ?? state.openEpoch;
        if (
          !conversationId ||
          !isCurrentConversationRequest(state, openEpoch, conversationId)
        ) {
          return null;
        }
        const response = await getAgentServiceMessages(conversationId, {
          limit: 100,
          includeAgentStatus: true,
        });
        const page = responseData(response, "无法读取客服消息");
        if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
          return null;
        }
        commit("setMessagePage", adaptPage(page, state.conversation));
        await dispatch('observeAgentProgress', { progress: response.agentStatus, conversationId, openEpoch });
        if (options.markRead !== false) {
          await dispatch("markVisibleMessagesRead", {
            conversationId,
            openEpoch,
          });
        }
        return page;
      },

      /** 只为当前最新请求反馈失败；后来的问题、关闭窗口和旧REST响应不能相互覆盖。 */
      async observeAgentProgress({ state, commit }, { progress, conversationId, openEpoch }) {
        const source = latestPersistedCustomerMessage(state);
        if (!progress?.status || !source || progress.businessMessageId !== source.businessMessageId
          || state.sending || !isCurrentConversationRequest(state, openEpoch, conversationId)
          || state.conversation?.controlState !== 'AI_ENABLED' || source.sequence <= state.cancelledThroughSequence) return;
        if (state.messages.some(message => Number(message.sequence)>Number(source.sequence) && ['AI_AGENT','BUSINESS_STAFF'].includes(message.actorType))) return;
        if (state.agentTerminalMessageId === source.businessMessageId && !state.agentError) return;
        if (['NOTIFIED', 'CANCELLED', 'SUPPRESSED'].includes(progress.status)) {
          commit('finishAgentRequest', { businessMessageId: source.businessMessageId, agentRunId: progress.agentRunId });
          return;
        }
        if (progress.status === 'UNAVAILABLE') {
          commit('incrementAgentStatusUnavailable');
          // 至少连续6次且持续60秒才显示可恢复提示；未知状态永远不自动终止生成。
          if (state.agentStatusUnavailableCount >= 6 && Date.now() - state.agentStatusUnavailableSince >= 60_000)
            commit('showAgentStatusUnavailable', progress.notice);
          return;
        }
        commit('clearAgentStatusUnavailable');
        if (progress.status !== 'FAILED') return;
        const businessMessageId = source.businessMessageId;
        const key = `${openEpoch}:${conversationId}:${businessMessageId}`;
        commit('finishAgentRequest', { businessMessageId, notice: progress.notice, failed: true, agentRunId: progress.agentRunId });
        if (noticeInFlight?.key === key || (state.failureNoticeAttempts[businessMessageId] || 0) >= 3) return;
        commit('incrementFailureNoticeAttempt', businessMessageId);
        const operation = acknowledgeAgentServiceFailure(conversationId, businessMessageId);
        noticeInFlight = { key, operation };
        try {
          const response = await operation;
          const page = responseData(response, progress.notice || '网络繁忙请稍后再试');
          if (!isCurrentConversationRequest(state, openEpoch, conversationId) || latestPersistedCustomerMessage(state)?.businessMessageId !== businessMessageId) return;
          if (response.conversation) commit('setConversation', adaptConversation(response.conversation));
          commit('setMessagePage', adaptPage(page, state.conversation));
          if (['NOTIFIED','SUPPRESSED','CANCELLED'].includes(response.agentStatus?.status)) commit('finishAgentRequest', { businessMessageId, agentRunId: progress.agentRunId });
        } catch { /* 通知落库不可达时保留固定失败提示；不自动重发客户问题。 */ }
        finally { if (noticeInFlight?.key === key) noticeInFlight = null; }
      },

      /** 根据服务端返回的游标读取更早消息。 */
      async loadOlderMessages({ state, commit }) {
        if (
          !state.conversation?.conversationId ||
          !state.hasMore ||
          !state.nextBeforeSequence
        ) {
          return;
        }
        const conversationId = state.conversation.conversationId;
        const openEpoch = state.openEpoch;
        const beforeSequence = state.nextBeforeSequence;
        commit("setLoadingOlder", true);
        try {
          const response = await getAgentServiceMessages(conversationId, {
            beforeSequence,
            limit: 30,
          });
          const page = responseData(response, "无法读取更早消息");
          if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
            return null;
          }
          commit("setMessagePage", adaptPage(page, state.conversation));
          return page;
        } finally {
          if (isCurrentConversationRequest(state, openEpoch, conversationId)) {
            commit("setLoadingOlder", false);
          }
        }
      },

      /** 乐观显示消息，服务端确认后用正式业务消息替换。 */
      async sendMessage(
        { state, commit, dispatch },
        { content, currentUserId, clientMessageId, contextId },
      ) {
        let conversation = state.conversation;
        if (!conversation) throw new Error("客服会话尚未建立");
        if (state.sending) throw new Error("消息正在发送，请稍候");
        if (state.controlChanging) throw new Error("客服控制状态正在更新，请稍候");
        const normalizedContent = String(content || "").trim();
        if (!normalizedContent) throw new Error("消息不能为空");
        const cancellation = replyCancellationFlights.get(conversation.conversationId);
        if (cancellation) {
          const waitingId = conversation.conversationId, waitingEpoch = state.openEpoch;
          commit("setSending", true);
          try { await cancellation; }
          finally { if (isCurrentConversationRequest(state, waitingEpoch, waitingId)) commit("setSending", false); }
          if (!isCurrentConversationRequest(state, waitingEpoch, waitingId)) return null;
          conversation = state.conversation;
        }
        const stableClientMessageId =
          clientMessageId || createAgentServiceClientMessageId();
        const policy = resolveAgentServiceControlPolicy(
          conversation,
          currentUserId,
          state.viewerRole,
        );
        const conversationId = conversation.conversationId;
        const openEpoch = state.openEpoch;
        if (!policy.canSend) throw new Error("当前会话状态不能发送消息");

        commit("removePendingMessage", stableClientMessageId);
        commit("addPendingMessage", {
          conversationId: conversation.conversationId,
          businessMessageId: `local:${stableClientMessageId}`,
          sequence: 0,
          actorType: policy.participantType,
          actorUserId: String(currentUserId),
          displayNameSnapshot: currentDisplayName(conversation, currentUserId),
          content: normalizedContent,
          ...(contextId ? { contextId } : {}),
          clientMessageId: stableClientMessageId,
          createdAt: new Date().toISOString(),
          localStatus: "SENDING",
        });
        commit("setSending", true);
        try {
          const response = await sendAgentServiceMessage(conversationId, {
            ...(state.viewerRole === 'RECEPTION' ? { viewerRole: state.viewerRole } : {}),
            clientMessageId: stableClientMessageId,
            content: normalizedContent,
            ...(conversation.controlVersion ? { expectedControlVersion: conversation.controlVersion } : {}),
            ...(contextId ? { contextId } : {}),
          });
          responseData(response, "消息发送失败");
          await dispatch("refreshMessages", { conversationId, openEpoch });
        } catch (error) {
          if (isCurrentConversationRequest(state, openEpoch, conversationId)) {
            commit("markPendingFailed", stableClientMessageId);
          }
          throw error;
        } finally {
          if (isCurrentConversationRequest(state, openEpoch, conversationId)) {
            commit("setSending", false);
          }
        }
      },

      /** 执行请求人工、接受接管或交回AI并更新本地控制版本。 */
      async changeControl({ state, commit }, { action, reason }) {
        const conversation = state.conversation;
        if (!conversation) throw new Error("客服会话尚未建立");
        if (state.controlChanging) {
          throw new Error("客服控制状态正在更新，请稍候");
        }
        const data = {
          ...(state.viewerRole === 'RECEPTION' ? { viewerRole: state.viewerRole } : {}),
          expectedControlVersion: conversation.controlVersion,
          ...(action === "request"
            ? {
                handoffReason:
                  reason ||
                  (sceneType === "GENERAL"
                    ? "需要人工客服协助"
                    : "需要业务员人工协助"),
              }
            : {}),
        };
        const calls = {
          request: requestAgentServiceHuman,
          accept: acceptAgentServiceHuman,
          release: releaseAgentServiceToAi,
          reject: rejectAgentServiceHandoff,
          disable: disableAgentServiceAi,
          enable: enableAgentServiceAi,
        };
        const call = calls[action];
        if (!call) throw new Error("不支持的客服控制动作");
        const conversationId = conversation.conversationId;
        const openEpoch = state.openEpoch;
        commit("setControlChanging", conversationId);
        try {
          const response = await call(conversationId, data);
          const result = responseData(response, "客服控制状态更新失败");
          if (!isCurrentConversationRequest(state, openEpoch, conversationId)) {
            return null;
          }
          commit("setConversation", applyControlResult(conversation, result));
          if (action === 'request' || action === 'disable') commit('cancelPendingReplyLocal', lastAgentServiceSequence(state.messages));
          return result;
        } finally {
          if (isCurrentConversationRequest(state, openEpoch, conversationId)) {
            commit("setControlChanging", "");
          }
        }
      },

      /** 处理WebSocket通知；消息正文始终重新从REST读取。 */
      async handleNotification({ state, commit, dispatch }, source) {
        const notification = adaptAgentServiceWebSocketNotification(source);
        if (
          String(notification.conversationId) !==
          String(state.conversation?.conversationId || "")
        ) {
          return;
        }
        const effect = reduceAgentServiceNotification(notification);
        if (notification.eventType === 'customer.conversation.control.changed.v1' && notification.previousControlState === 'AI_ENABLED' && notification.controlState === 'AI_ENABLED' && notification.controlVersion > state.conversation.controlVersion) {
          commit('cancelPendingReplyLocal', lastAgentServiceSequence(state.messages));
        }
        if (effect.agentError) {
          commit("setAgentError", {
            agentRunId: effect.agentRunId,
            error: effect.agentError,
          });
        }
        if (effect.agentProcessing !== undefined) {
          commit("setAgentProcessing", {
            value: effect.agentProcessing,
            agentRunId: effect.agentRunId,
          });
        }
        if (effect.refreshConversation) await dispatch("refreshConversation");
        if (effect.refreshMessages) {
          commit("setAgentProcessing", false);
          await dispatch("refreshMessages");
        }
      },

      /** 把当前可见的最大服务端sequence登记为已读。 */
      async markVisibleMessagesRead({ state, dispatch }, options = {}) {
        if (globalThis.document?.hidden) return null;
        if (!state.visible) return null;
        const conversationId =
          options.conversationId || state.conversation?.conversationId;
        const openEpoch = options.openEpoch ?? state.openEpoch;
        if (
          !conversationId ||
          !isCurrentConversationRequest(state, openEpoch, conversationId)
        ) {
          return null;
        }
        const sequence = lastAgentServiceSequence(state.messages);
        if (!sequence) return;
        const response = await updateAgentServiceReadCursor(
          conversationId,
          sequence,
          ...(state.viewerRole === 'RECEPTION' ? [state.viewerRole] : []),
        );
        requireAgentServiceSuccess(response);
        // 已读写入完成后再校准全局角标；汇总失败不改变原已读结果。
        if (dispatch) void dispatch("user/refreshUnreadCount", null, { root: true })
          .catch(() => console.warn("消息未读汇总刷新失败"));
        return response;
      },
    },
  };
  if (sceneType === "GENERAL") {
    // 同时覆盖REST业务404与HTTP404；旧请求失败不能删除新打开的会话。
    Object.entries(module.actions).forEach(([name, action]) => {
      module.actions[name] = async (context, payload) => {
        const epoch =
          context.state.openEpoch +
          (["initialize", "openConversation"].includes(name) ? 1 : 0);
        try {
          return await action(context, payload);
        } catch (error) {
          if (
            context.state.openEpoch === epoch &&
            (error.response?.status === 404 ||
              error.code === 404 ||
              error.errorCode === "CONVERSATION_NOT_FOUND" ||
              error.response?.data?.errorCode === "CONVERSATION_NOT_FOUND")
          ) {
            context.commit("markConversationMissing");
          }
          throw error;
        }
      };
    });
  }
  return module;
}

export default createAgentServiceStore();
